import type { EmsComponents } from '../../api/ems';
import type { HubMethodContract, IncidentId } from './common';
import { normalizePortalCall } from './call';
import type { PortalCall, PortalCallWire } from './call';
import { normalizeIncident } from './incident';
import type { Incident, IncidentWire } from './incident';
import type { AttachmentBody, EmergencySituationInstructions } from './chat';

type GeneratedPortalOverviewWire = EmsComponents['schemas']['PortalOverview'];

export type { PortalCall, PortalCallWire } from './call';
export type PortalOverviewWire = Omit<GeneratedPortalOverviewWire, 'calls'> & {
  calls: PortalCallWire[];
};
export type PortalOverview = {
  incidents: Incident[];
  calls: PortalCall[];
  recentIncidentsWindowSeconds: number;
  recentCallsWindowSeconds: number;
};

export function normalizePortalOverview(overview: PortalOverviewWire): PortalOverview {
  return {
    incidents: overview.incidents.map((incident) => normalizeIncident(incident)),
    calls: overview.calls.map(normalizePortalCall),
    recentIncidentsWindowSeconds: overview.recentIncidentsWindowSeconds,
    recentCallsWindowSeconds: overview.recentCallsWindowSeconds,
  };
}

export type IncidentLiveState = Incident['liveState'];
export type IncidentAppleEedCapabilities = Incident['appleEedCapabilities'][number];
export type IncidentCategory = Incident['agencyViews'][number]['category'];

export type ChatMessageBody = {
  message: string | null;
  emergencySituationInstructions: EmergencySituationInstructions;
  attachment: AttachmentBody | null;
};

export type PortalHubClientEvents = {
  IncidentChanged: (incident: IncidentWire) => void;
  CallChanged: (call: PortalCallWire) => void;
  IncidentCallChanged: (call: PortalCallWire) => void;
  CallerChatTextMessagePreviewReceived: (
    incidentId: IncidentId,
    messagePreview: string | null,
  ) => void;
  CallerChatImageMessagePreviewReceived: (incidentId: IncidentId, isSendingImage: boolean) => void;
  DispatcherChatTextMessagePreviewReceived: (
    incidentId: IncidentId,
    messagePreview: string | null,
  ) => void;
  DispatcherChatImageMessagePreviewReceived: (
    incidentId: IncidentId,
    isSendingImage: boolean,
  ) => void;
};

export const PORTAL_HUB_CLIENT_EVENTS = {
  IncidentChanged: { params: ['PortalIncident'], returns: 'Task' },
  CallChanged: { params: ['PortalCall'], returns: 'Task' },
  IncidentCallChanged: { params: ['PortalCall'], returns: 'Task' },
  CallerChatTextMessagePreviewReceived: { params: ['IncidentId', 'string?'], returns: 'Task' },
  CallerChatImageMessagePreviewReceived: { params: ['IncidentId', 'bool'], returns: 'Task' },
  DispatcherChatTextMessagePreviewReceived: { params: ['IncidentId', 'string?'], returns: 'Task' },
  DispatcherChatImageMessagePreviewReceived: { params: ['IncidentId', 'bool'], returns: 'Task' },
} as const satisfies Record<keyof PortalHubClientEvents, HubMethodContract>;

export type PortalHubServerMethods = {
  GetOverview: () => Promise<PortalOverviewWire>;
  GetIncident: (incidentId: IncidentId) => Promise<IncidentWire | null>;
  SetIsChatTranslationsAllowed: (
    incidentId: IncidentId,
    allowChatTranslations: boolean,
  ) => Promise<void>;
  SendChatMessage: (incidentId: IncidentId, body: ChatMessageBody) => Promise<void>;
  SendDispatcherChatTextMessagePreview: (
    incidentId: IncidentId,
    messagePreview: string,
  ) => Promise<void>;
  SendDispatcherChatImageMessagePreview: (
    incidentId: IncidentId,
    isSendingImage: boolean,
  ) => Promise<void>;
  EndLiveVideo: (incidentId: IncidentId) => Promise<void>;
  SwitchCameraRequest: (incidentId: IncidentId) => Promise<void>;
  TakeCallerCameraSnapshot: (incidentId: IncidentId) => Promise<void>;
  MuteRequest: (incidentId: IncidentId) => Promise<void>;
  UnmuteRequest: (incidentId: IncidentId) => Promise<void>;
};

export const PORTAL_HUB_SERVER_METHODS = {
  GetOverview: { params: [], returns: 'Task<PortalOverview>' },
  GetIncident: { params: ['IncidentId'], returns: 'Task<PortalIncident?>' },
  SetIsChatTranslationsAllowed: { params: ['IncidentId', 'bool'], returns: 'Task' },
  SendChatMessage: { params: ['IncidentId', 'ChatMessageBody'], returns: 'Task' },
  SendDispatcherChatTextMessagePreview: { params: ['IncidentId', 'string'], returns: 'Task' },
  SendDispatcherChatImageMessagePreview: { params: ['IncidentId', 'bool'], returns: 'Task' },
  EndLiveVideo: { params: ['IncidentId'], returns: 'Task' },
  SwitchCameraRequest: { params: ['IncidentId'], returns: 'Task' },
  TakeCallerCameraSnapshot: { params: ['IncidentId'], returns: 'Task' },
  MuteRequest: { params: ['IncidentId'], returns: 'Task' },
  UnmuteRequest: { params: ['IncidentId'], returns: 'Task' },
} as const satisfies Record<keyof PortalHubServerMethods, HubMethodContract>;
